/**
 * 목록과 상세 사이의 경계 (2026-08-21) — 끌어서 비율을 바꾼다.
 *
 * 좌우열림이면 세로 막대(좌우로 끔), 상하열림이면 가로 막대(위아래로 끔).
 * 비율은 기기에 기억한다 — 사람마다 목록을 넓게 쓰는 쪽과 본문을 넓게 쓰는 쪽이 갈린다.
 *
 * ★ 끌 때는 전환(transition)을 끈다. 안 끄면 패널이 손끝을 280ms 뒤에서 따라온다.
 * ★ 양쪽 모두 최소 크기를 지킨다 — 한쪽을 0 으로 만들 수 있으면 되돌릴 방법이 사라진다.
 * ★ 마우스만이 아니라 키보드로도 움직인다(role="separator"): ← → · ↑ ↓ 로 2%,
 *   Home 으로 기본값. 두 번 누르기(double click)도 기본값으로 되돌린다.
 */

import { useCallback, useRef } from 'react';
import { clampMailRatio, DEFAULT_MAIL_VIEW } from './mailView';
import { tMail } from '../../lib/mail-i18n';

/** 목록·상세 어느 쪽도 이보다 좁아지지 않는다. */
const MIN_LIST_PX = 300;
const MIN_DETAIL_PX = 360;
const MIN_LIST_PX_V = 150;
const MIN_DETAIL_PX_V = 220;

interface MailSplitterProps {
  orientation: 'vertical' | 'horizontal';
  ratio: number;
  onRatio: (next: number) => void;
  /** 끌기가 끝났을 때 — 여기서만 저장한다(끄는 동안 저장하면 기기 저장이 초당 수십 번 돈다). */
  onCommit: (next: number) => void;
  onResizing: (on: boolean) => void;
}

export function MailSplitter({ orientation, ratio, onRatio, onCommit, onResizing }: MailSplitterProps) {
  const lastRef = useRef(ratio);
  const vertical = orientation === 'vertical';

  const clampToBox = useCallback((raw: number, boxPx: number) => {
    const minList = (vertical ? MIN_LIST_PX : MIN_LIST_PX_V) / boxPx;
    const maxList = 1 - (vertical ? MIN_DETAIL_PX : MIN_DETAIL_PX_V) / boxPx;
    // 상자가 아주 작으면 최소끼리 뒤집힌다 — 그때는 반으로 둔다.
    if (minList >= maxList) return 0.5;
    return Math.min(maxList, Math.max(minList, clampMailRatio(raw)));
  }, [vertical]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button > 0) return;
    e.preventDefault();
    const box = (e.currentTarget as HTMLElement).closest('.mail-main') as HTMLElement | null;
    if (!box) return;
    onResizing(true);

    const move = (ev: PointerEvent) => {
      const r = box.getBoundingClientRect();
      const boxPx = vertical ? r.width : r.height;
      if (boxPx <= 0) return;
      const raw = vertical ? (ev.clientX - r.left) / boxPx : (ev.clientY - r.top) / boxPx;
      const next = clampToBox(raw, boxPx);
      lastRef.current = next;
      onRatio(next);
    };
    const up = () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
      onResizing(false);
      onCommit(lastRef.current);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', up);
  }, [vertical, clampToBox, onRatio, onCommit, onResizing]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    const back = vertical ? 'ArrowLeft' : 'ArrowUp';
    const fwd = vertical ? 'ArrowRight' : 'ArrowDown';
    let next: number | null = null;
    if (e.key === back) next = ratio - 0.02;
    else if (e.key === fwd) next = ratio + 0.02;
    else if (e.key === 'Home') next = vertical ? DEFAULT_MAIL_VIEW.sideRatio : DEFAULT_MAIL_VIEW.bottomRatio;
    if (next === null) return;
    e.preventDefault();
    const box = (e.currentTarget as HTMLElement).closest('.mail-main') as HTMLElement | null;
    const r = box?.getBoundingClientRect();
    const boxPx = r ? (vertical ? r.width : r.height) : 0;
    const value = boxPx > 0 ? clampToBox(next, boxPx) : clampMailRatio(next);
    lastRef.current = value;
    onRatio(value);
    onCommit(value);
  }, [vertical, ratio, clampToBox, onRatio, onCommit]);

  const reset = useCallback(() => {
    const value = vertical ? DEFAULT_MAIL_VIEW.sideRatio : DEFAULT_MAIL_VIEW.bottomRatio;
    lastRef.current = value;
    onRatio(value);
    onCommit(value);
  }, [vertical, onRatio, onCommit]);

  return (
    <div
      className="mail-splitter"
      role="separator"
      tabIndex={0}
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
      aria-label={tMail('mail.split.aria')}
      aria-valuenow={Math.round(ratio * 100)}
      aria-valuemin={25}
      aria-valuemax={75}
      title={tMail('mail.split.title')}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onDoubleClick={reset}
    >
      <span className="mail-splitter-grip" aria-hidden />
    </div>
  );
}
